import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

/** Approximate centroids for Cote d'Ivoire cities and Abidjan neighbourhoods.
 * Lat/Lng kept loose on purpose — we display a zone, never the exact address. */
const CITY_COORDS = {
  "Abidjan":      { lat: 5.345317, lng: -4.024429, zoom: 11 },
  "Bouake":       { lat: 7.690000, lng: -5.030000, zoom: 12 },
  "Yamoussoukro": { lat: 6.827623, lng: -5.289343, zoom: 12 },
  "San Pedro":    { lat: 4.748590, lng: -6.636330, zoom: 12 },
  "Korhogo":      { lat: 9.458210, lng: -5.629730, zoom: 12 },
  "Daloa":        { lat: 6.877900, lng: -6.450000, zoom: 12 },
  "Man":          { lat: 7.412000, lng: -7.554000, zoom: 12 },
  "Gagnoa":       { lat: 6.131900, lng: -5.948500, zoom: 12 },
  "Abengourou":   { lat: 6.729460, lng: -3.495440, zoom: 12 },
  "Bondoukou":    { lat: 8.039940, lng: -2.795260, zoom: 12 },
  "Divo":         { lat: 5.837050, lng: -5.358830, zoom: 12 },
  "Soubre":       { lat: 5.783330, lng: -6.600000, zoom: 12 },
  "Odienne":      { lat: 9.500000, lng: -7.566670, zoom: 12 },
  // Localités à fort potentiel foncier — coordonnées approximatives
  "Grand-Bassam":    { lat: 5.196700, lng: -3.738900, zoom: 13 },
  "Assinie":         { lat: 5.144000, lng: -3.298000, zoom: 13 },
  "Assouinde":       { lat: 5.148000, lng: -3.412000, zoom: 13 },
  "Mondoukou":       { lat: 5.205000, lng: -3.706000, zoom: 13 },
  "Jacqueville":     { lat: 5.200000, lng: -4.420000, zoom: 13 },
  "Dabou":           { lat: 5.323000, lng: -4.379000, zoom: 12 },
  "Bingerville":     { lat: 5.355000, lng: -3.882000, zoom: 13 },
  "Ile Boulay":      { lat: 5.274000, lng: -4.117000, zoom: 13 },
  "Ile Vitre":       { lat: 5.140000, lng: -3.207000, zoom: 13 },
  "Bregbo":          { lat: 5.358000, lng: -3.877000, zoom: 14 },
  "Eloka-Te":        { lat: 5.358000, lng: -3.835000, zoom: 14 },
  "Asseoufoue":      { lat: 5.367000, lng: -3.864000, zoom: 14 },
  "Motobe":          { lat: 5.350000, lng: -3.870000, zoom: 14 },
  "Yaou":            { lat: 5.405000, lng: -4.090000, zoom: 13 },
  "Attinguie":       { lat: 5.503000, lng: -4.149000, zoom: 13 },
  "Samo":            { lat: 5.330000, lng: -4.453000, zoom: 13 },
  "Sassako-Begniny": { lat: 5.358000, lng: -4.184000, zoom: 13 },
};

const QUARTIER_COORDS_ABJ = {
  "Cocody":      { lat: 5.358200, lng: -3.985000 },
  "Riviera":     { lat: 5.371000, lng: -3.953000 },
  "2 Plateaux":  { lat: 5.376000, lng: -4.001000 },
  "Angre":       { lat: 5.402000, lng: -3.970000 },
  "Palmeraie":   { lat: 5.366000, lng: -3.918000 },
  "Marcory":     { lat: 5.295000, lng: -4.003000 },
  "Bietry":      { lat: 5.295600, lng: -3.987100 },
  "Zone 4":      { lat: 5.302100, lng: -4.001400 },
  "Plateau":     { lat: 5.323900, lng: -4.020900 },
  "Treichville": { lat: 5.293000, lng: -4.013000 },
  "Yopougon":    { lat: 5.345000, lng: -4.090000 },
  "Abobo":       { lat: 5.418000, lng: -4.030000 },
  "Adjame":      { lat: 5.353000, lng: -4.029000 },
  "Attiecoube":  { lat: 5.357000, lng: -4.043000 },
  "Koumassi":    { lat: 5.292400, lng: -3.954800 },
  "Port-Bouet":  { lat: 5.255000, lng: -3.945000 },
  "Vridi":       { lat: 5.255000, lng: -3.985000 },
  "Bingerville": { lat: 5.355000, lng: -3.882000 },
  "Anyama":      { lat: 5.494200, lng: -4.051600 },
  "Songon":      { lat: 5.330000, lng: -4.250000 },
};

function resolveCoords(city, quartier) {
  if (city === "Abidjan" && quartier && QUARTIER_COORDS_ABJ[quartier]) {
    return { ...QUARTIER_COORDS_ABJ[quartier], zoom: 14, source: "quartier" };
  }
  if (city && CITY_COORDS[city]) {
    return { ...CITY_COORDS[city], source: "city" };
  }
  return null;
}

export default function LocationMap({ city, quartier }) {
  const coords = useMemo(() => resolveCoords(city, quartier), [city, quartier]);

  // Fix default leaflet icon paths (CRA does not serve them by default).
  useEffect(() => {
    // No icon needed because we use CircleMarker, but ensure the CSS is loaded.
  }, []);

  if (!coords) return null;

  const label = quartier && city === "Abidjan" ? `${quartier} (Abidjan)` : city;

  return (
    <section className="mt-12" data-testid="annonce-map-section">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-1 inline-flex items-center gap-2">
        <MapPin size={20} className="text-orange-500" />
        Localisation
      </h2>
      <p className="text-[var(--ink-soft)] mb-4 text-sm">
        Zone approximative — l&apos;adresse exacte est partagee uniquement lors du contact direct avec le vendeur.
      </p>
      <div className="rounded-2xl overflow-hidden border border-[var(--line)] shadow-sm" style={{ height: 320 }} data-testid="annonce-map-container">
        <MapContainer
          center={[coords.lat, coords.lng]}
          zoom={coords.zoom}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CircleMarker
            center={[coords.lat, coords.lng]}
            radius={coords.source === "quartier" ? 90 : 140}
            pathOptions={{ color: "#f97316", fillColor: "#fb923c", fillOpacity: 0.35, weight: 2 }}
          >
            <Popup>{label}</Popup>
          </CircleMarker>
        </MapContainer>
      </div>
    </section>
  );
}
